/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type React from 'react';
import { cn } from '@/lib/utils';
import { useAnalytics } from '@/hooks/use-analytics';
import { resolveImageUrl } from '@/lib/images/resolve-image-url';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';

interface Suggestion {
    name: string;
    link: string;
    type: string;
    image?: string;
    parentCategoryName?: string;
}

interface SuggestionsProps {
    suggestions?: Suggestion[];
    searchPhrase?: string;
    closeAndNavigate?: (link: string) => void;
    className?: string;
    variant?: 'default' | 'category';
}

const Suggestions: React.FC<SuggestionsProps> = ({
    suggestions,
    searchPhrase,
    closeAndNavigate,
    className,
    variant = 'default',
}) => {
    const analytics = useAnalytics();
    const config = useConfig();
    const { t } = useTranslation('common');
    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleClick = (suggestion: Suggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        if (closeAndNavigate) {
            closeAndNavigate(suggestion.link);
        }
    };

    const isCategory = variant === 'category';

    return (
        <div data-testid="sf-suggestion" className={cn(isCategory ? 'flex flex-col gap-3' : 'space-y-0', className)}>
            {suggestions.map((suggestion) => {
                const imageUrl = suggestion.image
                    ? resolveImageUrl({ src: suggestion.image, config, context: 'search' })
                    : '';

                return (
                <button
                    key={suggestion.link}
                    data-slot="suggestion"
                    onMouseDown={() => handleClick(suggestion)}
                    className={cn(
                        'mt-0 flex items-center justify-start py-0',
                        isCategory
                            ? 'w-fit text-base font-normal'
                            : 'w-full pl-4 text-sm transition-colors hover:bg-accent hover:text-foreground'
                    )}>
                    <div className="flex items-center">
                        {imageUrl ? (
                            <div className="w-10 h-8 mr-4 rounded-full bg-transparent flex items-center justify-center overflow-hidden shrink-0">
                                <img
                                    src={imageUrl}
                                    alt={suggestion.name || t('suggestionImageAlt')}
                                    className="h-full w-full object-cover rounded-full"
                                    loading="eager"
                                />
                            </div>
                        ) : null}
                        <div className="text-left">
                            <span className={cn(isCategory ? 'text-base font-normal' : 'text-sm font-medium')}>
                                {suggestion.name}
                            </span>
                        </div>
                    </div>
                </button>
                );
            })}
        </div>
    );
};

export default Suggestions;
